import React, { useState, useRef, useEffect } from 'react';
import { X, ZoomIn, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';

/**
 * Crop modal. Given a list of images, lets the user pan/zoom each one inside a
 * frame of a chosen aspect ratio, then returns JPEG crops via onConfirm. Used to
 * frame vertical/horizontal source photos so they fill a target area instead of
 * letterboxing (PDP square photo area; Facebook feed 4:5 / 1:1 / 1.91:1).
 *
 * Props:
 *   images:    [{ url, name, alt }]  — url MUST be same-origin so the export
 *              canvas isn't tainted (a relative /.netlify/functions/... URL is
 *              fine). Use r2-resize, not r2-image: r2-image base64-buffers the
 *              master and 502s past Netlify's ~6 MB response cap on big photos.
 *   onCancel()
 *   onConfirm(items)  — items: [{ file, alt, sourceKey?, recropImageId? }]
 *                       (image/jpeg), one per image that loaded OK; images that
 *                       failed are skipped. sourceKey / recropImageId pass
 *                       straight through from the matching input image.
 *   initialAspect      — '1:1' (default) | '4:5' | '1.91:1'
 *   allowAspectChange  — when true, shows the aspect toggle (FB use). Default
 *                        false → fixed to initialAspect (PDP square use).
 */
const ASPECTS = [
  { key: '9:16', label: 'Story 9:16', ratio: 9 / 16 },
  { key: '4:5', label: 'Portrait 4:5', ratio: 4 / 5 },
  { key: '1:1', label: 'Square 1:1', ratio: 1 },
  { key: '1.91:1', label: 'Landscape 1.91:1', ratio: 1.91 },
];
const OUTPUT_LONG = 1600; // px — longest edge of the written JPEG
const MAX_W = 360;        // px — on-screen frame caps
const MAX_H = 420;

// Frame pixel dims for an aspect ratio (w/h), longest side capped.
const frameDims = (ratio) => {
  const VW = Math.round(Math.min(MAX_W, MAX_H * ratio));
  return { VW, VH: Math.round(VW / ratio) };
};

// Scale that fits the WHOLE image inside the frame (letterboxed).
const containScaleFor = (im, VW, VH) => Math.min(VW / im.naturalWidth, VH / im.naturalHeight);
// Scale that fills the frame (image bleeds past two edges).
const coverScaleFor = (im, VW, VH) => Math.max(VW / im.naturalWidth, VH / im.naturalHeight);

// Open showing the whole photo, centered. The zoom slider then runs from here
// (full image) up through "fills the frame" and beyond.
const makeInitial = (im, VW, VH) => {
  const cs = containScaleFor(im, VW, VH);
  return {
    scale: cs, minScale: cs, coverScale: coverScaleFor(im, VW, VH),
    ox: (VW - im.naturalWidth * cs) / 2,
    oy: (VH - im.naturalHeight * cs) / 2,
  };
};

// Keep the image in bounds: when an axis is larger than the frame, no gap on
// that edge; when smaller, keep it inside the frame.
const clampT = (t, im, VW, VH) => {
  const w = im.naturalWidth * t.scale;
  const h = im.naturalHeight * t.scale;
  const fit = (v, size, vp) => {
    const lo = Math.min(0, vp - size);
    const hi = Math.max(0, vp - size);
    return Math.min(hi, Math.max(lo, v));
  };
  return { ...t, ox: fit(t.ox, w, VW), oy: fit(t.oy, h, VH) };
};

export default function ImageCropModal({ images, onCancel, onConfirm, initialAspect = '1:1', allowAspectChange = false }) {
  const [aspectKey, setAspectKey] = useState(ASPECTS.some(a => a.key === initialAspect) ? initialAspect : '1:1');
  const ratio = (ASPECTS.find(a => a.key === aspectKey) || ASPECTS[1]).ratio;
  const { VW, VH } = frameDims(ratio);
  const outK = OUTPUT_LONG / Math.max(VW, VH);
  const OUT_W = Math.round(VW * outK);
  const OUT_H = Math.round(VH * outK);
  const dimsRef = useRef({ VW, VH });
  dimsRef.current = { VW, VH };

  const [idx, setIdx] = useState(0);
  const [els, setEls] = useState(() => images.map(() => null));          // HTMLImageElement | null
  const [errs, setErrs] = useState(() => images.map(() => false));        // failed-to-load flags
  const [transforms, setTransforms] = useState(() => images.map(() => null));
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const drag = useRef(null);

  // Load every picked image once. Initial transform uses the live dims (ref) so
  // an aspect toggle before load doesn't bake in stale geometry.
  useEffect(() => {
    let cancelled = false;
    images.forEach((img, i) => {
      const im = new Image();
      im.onload = () => {
        if (cancelled) return;
        setEls((p) => { const n = p.slice(); n[i] = im; return n; });
        setTransforms((p) => {
          if (p[i]) return p;
          const { VW: w, VH: h } = dimsRef.current;
          const n = p.slice(); n[i] = makeInitial(im, w, h); return n;
        });
      };
      im.onerror = () => {
        if (cancelled) return;
        setErrs((p) => { const n = p.slice(); n[i] = true; return n; });
      };
      im.src = img.url;
    });
    return () => { cancelled = true; };
  }, [images]);

  // Changing the aspect re-fits every already-loaded image to the new frame.
  useEffect(() => {
    setTransforms((p) => p.map((tr, i) => (els[i] ? makeInitial(els[i], VW, VH) : tr)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspectKey]);

  const el = els[idx];
  const t = transforms[idx];
  const failed = errs[idx];
  const allSettled = images.every((_, i) => els[i] || errs[i]);
  const okCount = els.filter(Boolean).length;
  const failedCount = errs.filter(Boolean).length;

  const setActiveT = (updater) => {
    setTransforms((p) => {
      const im = els[idx];
      const cur = p[idx];
      if (!im || !cur) return p;
      const n = p.slice();
      n[idx] = clampT(typeof updater === 'function' ? updater(cur) : updater, im, VW, VH);
      return n;
    });
  };

  const onPointerDown = (e) => {
    if (!t) return;
    drag.current = { x: e.clientX, y: e.clientY, ox: t.ox, oy: t.oy };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    setActiveT((cur) => ({ ...cur, ox: d.ox + (e.clientX - d.x), oy: d.oy + (e.clientY - d.y) }));
  };
  const endDrag = (e) => {
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  // Zoom around the frame's center so the focal point stays put.
  const zoomTo = (newScale) => {
    if (!t) return;
    const cx = (VW / 2 - t.ox) / t.scale;
    const cy = (VH / 2 - t.oy) / t.scale;
    setActiveT((cur) => ({
      ...cur,
      scale: newScale,
      ox: VW / 2 - cx * newScale,
      oy: VH / 2 - cy * newScale,
    }));
  };
  const onWheel = (e) => {
    if (!t) return;
    e.preventDefault();
    const next = t.scale * (e.deltaY < 0 ? 1.1 : 1 / 1.1);
    zoomTo(Math.min(t.coverScale * 6, Math.max(t.minScale, next)));
  };

  // Render one image's crop to a JPEG blob at the current aspect.
  const renderBlob = (im, tr) => new Promise((resolve) => {
    const c = document.createElement('canvas');
    c.width = OUT_W;
    c.height = OUT_H;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUT_W, OUT_H);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const k = OUT_W / VW; // uniform (OUT_W/VW === OUT_H/VH)
    ctx.drawImage(im, tr.ox * k, tr.oy * k, im.naturalWidth * tr.scale * k, im.naturalHeight * tr.scale * k);
    c.toBlob((b) => resolve(b), 'image/jpeg', 0.9);
  });

  const handleSave = async () => {
    setBusy(true);
    setSaveErr(null);
    try {
      const items = [];
      for (let i = 0; i < images.length; i++) {
        const im = els[i];
        if (!im) continue; // skip images that failed to load
        const tr = transforms[i] || makeInitial(im, VW, VH);
        const blob = await renderBlob(im, tr);
        if (!blob) throw new Error('Could not process an image.');
        items.push({
          file: new File([blob], images[i].name || `media-${i + 1}.jpg`, { type: 'image/jpeg' }),
          alt: images[i].alt || '',
          sourceKey: images[i].sourceKey || null,
          recropImageId: images[i].recropImageId || null,
        });
      }
      if (items.length === 0) throw new Error('None of the picked photos could be loaded.');
      onConfirm(items);
    } catch (e) {
      setSaveErr(e.message);
      setBusy(false);
    }
  };

  const saveDisabled = busy || !allSettled || okCount === 0;
  const saveLabel = busy
    ? 'Processing…'
    : !allSettled
      ? 'Loading…'
      : `Save ${okCount} photo${okCount === 1 ? '' : 's'}`;

  return (
    <div onClick={busy ? undefined : onCancel} style={backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modal}>
        <div style={header}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#00395C' }}>Adjust photos — fill the frame</h3>
          <button onClick={onCancel} disabled={busy} style={iconBtn}><X size={18} color="#6e6e73" /></button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          {allowAspectChange && (
            <div style={{ display: 'flex', gap: 6 }}>
              {ASPECTS.map((a) => (
                <button
                  key={a.key}
                  onClick={() => setAspectKey(a.key)}
                  style={{
                    fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 16, cursor: 'pointer', fontFamily: 'Montserrat',
                    background: aspectKey === a.key ? '#00395C' : '#fff',
                    color: aspectKey === a.key ? '#fff' : '#00395C',
                    border: `1px solid ${aspectKey === a.key ? '#00395C' : '#cdd3da'}`,
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}

          <div style={{ fontSize: 12, color: '#6e6e73', textAlign: 'center', maxWidth: 360 }}>
            Starts on the whole photo. Drag to reposition and zoom in until it fills the frame — whatever is inside the frame is exactly what gets posted.
          </div>

          <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onWheel={onWheel}
            style={{ ...viewport, width: VW, height: VH, cursor: el && t ? (drag.current ? 'grabbing' : 'grab') : 'default' }}
          >
            {el && t ? (
              <img
                src={images[idx].url}
                alt=""
                draggable={false}
                style={{
                  position: 'absolute', left: t.ox, top: t.oy,
                  width: el.naturalWidth * t.scale, height: el.naturalHeight * t.scale,
                  maxWidth: 'none', userSelect: 'none',
                }}
              />
            ) : failed ? (
              <div style={centerMsg}>
                <AlertTriangle size={20} color="#c0392b" />
                <div style={{ marginTop: 6 }}>This photo couldn’t load — it will be skipped.</div>
              </div>
            ) : (
              <div style={centerMsg}>Loading…</div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: Math.max(VW, 280) }}>
            <ZoomIn size={15} color="#6e6e73" />
            <input
              type="range"
              disabled={!t}
              min={t ? t.minScale : 1}
              max={t ? t.coverScale * 6 : 6}
              step="0.0001"
              value={t ? t.scale : 1}
              onChange={(e) => zoomTo(parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: '#DC582A' }}
            />
          </div>

          {images.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} style={navBtn}>
                <ChevronLeft size={16} />
              </button>
              <div style={{ fontSize: 12, color: '#1d1d1f', minWidth: 96, textAlign: 'center' }}>
                Photo {idx + 1} of {images.length}
              </div>
              <button onClick={() => setIdx((i) => Math.min(images.length - 1, i + 1))} disabled={idx === images.length - 1} style={navBtn}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {images.length > 1 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  title={`Photo ${i + 1}`}
                  style={{
                    width: 44, height: 44, padding: 0, borderRadius: 4, overflow: 'hidden', cursor: 'pointer',
                    position: 'relative',
                    border: i === idx ? '2px solid #DC582A' : `1px solid ${errs[i] ? '#c0392b' : '#e5e5ea'}`,
                    background: '#f0f0f3', opacity: errs[i] ? 0.55 : 1,
                  }}
                >
                  <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          )}

          {failedCount > 0 && allSettled ? (
            <div style={{ color: '#c0392b', fontSize: 12 }}>
              {failedCount} photo{failedCount === 1 ? '' : 's'} couldn’t load and will be skipped.
            </div>
          ) : null}
          {saveErr ? <div style={{ color: '#c33', fontSize: 12 }}>{saveErr}</div> : null}
        </div>

        <div style={footer}>
          <button onClick={onCancel} disabled={busy} style={cancelBtn}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={saveDisabled}
            style={{ ...saveBtn, opacity: saveDisabled ? 0.6 : 1, cursor: saveDisabled ? 'not-allowed' : 'pointer' }}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1100,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};
const modal = {
  background: '#fff', borderRadius: 10, width: '100%', maxWidth: 460, maxHeight: '92vh',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const header = {
  padding: '14px 18px', borderBottom: '1px solid #e5e5ea',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
const footer = {
  padding: '12px 18px', borderTop: '1px solid #e5e5ea', background: '#fafafa',
  display: 'flex', justifyContent: 'flex-end', gap: 8,
};
const viewport = {
  // White background so the on-screen frame matches the saved image,
  // which is white-padded wherever the photo doesn't cover it.
  position: 'relative', overflow: 'hidden',
  borderRadius: 8, background: '#fff', touchAction: 'none',
  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
};
const centerMsg = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  height: '100%', color: '#6e6e73', fontSize: 12.5, textAlign: 'center', padding: 16,
};
const iconBtn = { background: 'none', border: 'none', padding: 4, cursor: 'pointer' };
const navBtn = {
  width: 28, height: 28, borderRadius: 4, border: '1px solid #e5e5ea', background: '#fff',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#00395C',
};
const cancelBtn = {
  background: '#fff', color: '#6e6e73', border: '1px solid #e5e5ea',
  borderRadius: 6, padding: '8px 14px', fontSize: 13, cursor: 'pointer',
};
const saveBtn = {
  background: '#DC582A', color: '#fff', border: 'none', borderRadius: 6,
  padding: '8px 18px', fontSize: 13, fontWeight: 600,
};
